// 監控範本的 API（kmgr M5-02；SPEC #kinio-kms-api、#kinio-kms-templates-impl）：都走 kmgr token（../api.ts kmgrFetch），mock 開關同 KMS 操作台。
//
//   GET·POST /v1/orgs/{org}/kmgr/templates · GET·PUT·DELETE …/{template} · GET …/{template}/versions[/{v}]
//   POST …/{template}/apply/preview {home_ids, version} → preview_id（10 分鐘；一次 ≤ 200 戶，畫面自動分批）
//   POST …/{template}/apply {home_ids, version, preview_id, overwrite} → 202 {job_id}（step-up：403 REAUTH_REQUIRED 由 kmgrFetch 處理）
//   GET …/{template}/apply-jobs[/{job}] · POST …/{job}/cancel · POST …/{job}/retry（step-up）
//   GET /v1/homes/{home}/kmgr/template（kmgr.read：這個家戶套用的版本快照、在地修改）
import { kmgrFetch } from '../api';
import type { HomeTemplate, Overwrite, Preview, Template, TemplateInput, TemplateJob, TemplateJobSummary, Names } from './types';

const enc = encodeURIComponent;
const base = (org: string) => `/v1/orgs/${enc(org)}/kmgr/templates`;
const one = (org: string, id: string) => `${base(org)}/${enc(id)}`;

/** 一次預覽 / 套用的家戶上限（kmgr 同值）。 */
export const PREVIEW_BATCH = 200;

export const tplApi = {
  list: async (api: string, org: string, archived = false): Promise<{ templates: Template[]; names?: Names }> => {
    const w = await kmgrFetch<{ templates?: Template[]; names?: Names }>(api, base(org), { query: { archived: archived ? '1' : undefined } });
    return { templates: w?.templates ?? [], names: w?.names };
  },
  get: (api: string, org: string, id: string) => kmgrFetch<Template>(api, one(org, id)),
  create: (api: string, org: string, body: TemplateInput) => kmgrFetch<Template>(api, base(org), { method: 'POST', body }),
  /** 內容變了版本 + 1；expected_version 不符 409 CONFLICT。 */
  put: (api: string, org: string, id: string, body: TemplateInput) => kmgrFetch<Template>(api, one(org, id), { method: 'PUT', body }),
  /** 封存（套過的家戶留著紀錄，不能再套用）。 */
  archive: (api: string, org: string, id: string) => kmgrFetch<void>(api, one(org, id), { method: 'DELETE' }),
  preview: (api: string, org: string, id: string, homeIds: string[], version?: number) =>
    kmgrFetch<Preview>(api, `${one(org, id)}/apply/preview`, { method: 'POST', body: { home_ids: homeIds, version } }),
  apply: (api: string, org: string, id: string, body: { home_ids: string[]; version: number; preview_id: string; overwrite: Overwrite[] }) =>
    kmgrFetch<{ job_id: string; preview_id: string; created: boolean }>(api, `${one(org, id)}/apply`, { method: 'POST', body }),
  jobs: async (api: string, org: string, id: string): Promise<TemplateJobSummary[]> =>
    (await kmgrFetch<{ jobs?: TemplateJobSummary[] }>(api, `${one(org, id)}/apply-jobs`))?.jobs ?? [],
  job: (api: string, org: string, id: string, job: string) => kmgrFetch<TemplateJob>(api, `${one(org, id)}/apply-jobs/${enc(job)}`),
  cancel: (api: string, org: string, id: string, job: string) => kmgrFetch<TemplateJob>(api, `${one(org, id)}/apply-jobs/${enc(job)}/cancel`, { method: 'POST' }),
  retry: (api: string, org: string, id: string, job: string) => kmgrFetch<TemplateJob>(api, `${one(org, id)}/apply-jobs/${enc(job)}/retry`, { method: 'POST' }),
  homeTemplate: (api: string, home: string) => kmgrFetch<HomeTemplate>(api, `/v1/homes/${enc(home)}/kmgr/template`),
};
